import { Intent } from '@blueprintjs/core';
import { query as stardogQuery } from 'stardog';
import { MimeType } from 'vet-bones/constants';

import { DatabasesActionType } from 'src/actions/databases/actionTypes';
import { ProjectsActionType } from 'src/actions/projects/actionTypes';
import { request } from 'src/actions/request/request';
import { FailureResponse, SuccessResponse } from 'src/actions/request/types';
import {
  ActionCreatorAction,
  FluxStandardAction,
  createTypedAction,
} from 'src/actions/types';
import { ProjectPropertyType } from 'src/constants/project';
import { DiagramSnapshot } from 'src/containers/ProjectDiagram/types/diagram';
import {
  Project,
  ProjectFileUpload,
  ProjectModel,
  ProjectVirtualGraph,
  ProjectVirtualGraphField,
  PublishHistory,
} from 'src/store/state/projects';
import { buildLabelsAndDescriptionsQuery } from 'src/templates/queries/labelsAndDescriptions';
import { modelClassesQuery } from 'src/templates/queries/modelClasses';
import { modelPropertiesQuery } from 'src/templates/queries/modelProperties';
import { modelRulesQuery } from 'src/templates/queries/modelRules';

export type PublishProjectResponse =
  | {
      success: true;
      action: SuccessResponse<any, any, DatabasesActionType>;
      actionDescription?: never;
      message: string;
    }
  | {
      success: false;
      action: FailureResponse<any, DatabasesActionType>;
      actionDescription: string;
      message: string;
    };

export const projectsActionCreators = {
  addedProject: ({
    id,
    name,
    dateCreated,
  }: Pick<Project, 'id' | 'name'> & Partial<Pick<Project, 'dateCreated'>>) =>
    createTypedAction(ProjectsActionType.ADDED_PROJECT, {
      id,
      name,
      dateCreated,
    }),
  addedFileUploadToProject: (
    id: string,
    fileUpload: Omit<ProjectFileUpload, 'dateLastUpdated'>
  ) =>
    createTypedAction(ProjectsActionType.ADDED_FILE_UPLOAD_TO_PROJECT, {
      id,
      fileUpload,
    }),
  addedVirtualGraphsToProject: (
    id: string,
    ...virtualGraphs: Array<Omit<ProjectVirtualGraph, 'dateLastUpdated'>>
  ) =>
    createTypedAction(ProjectsActionType.ADDED_VIRTUAL_GRAPHS_TO_PROJECT, {
      id,
      virtualGraphs,
    }),
  importedInvalidProjectFromFile: () =>
    createTypedAction(ProjectsActionType.IMPORTED_INVALID_PROJECT_FROM_FILE),
  importedProjectFromFile: (project: Project) =>
    createTypedAction(ProjectsActionType.IMPORTED_PROJECT_FROM_FILE, {
      project,
    }),
  updatedProjectVirtualGraphPreview: (
    projectId: string,
    resourceId: string,
    fields: ProjectVirtualGraphField[],
    previewRows: string[][],
    connectionId: string
  ) =>
    createTypedAction(
      ProjectsActionType.UPDATED_PROJECT_VIRTUAL_GRAPH_PREVIEW,
      {
        projectId,
        resourceId,
        fields,
        previewRows,
        connectionId,
      }
    ),
  updatedProjectVirtualGraphPreviewFailure: (
    message: string,
    isWarning?: boolean
  ) =>
    createTypedAction(
      ProjectsActionType.UPDATED_PROJECT_VIRTUAL_GRAPH_PREVIEW_ERROR,
      { message, isWarning }
    ),
  updatedProjectPropertyType: (
    projectId: string,
    propertyType: ProjectPropertyType
  ) =>
    createTypedAction(ProjectsActionType.UPDATED_PROJECT_PROPERTY_TYPE, {
      projectId,
      propertyType,
    }),
  duplicatedProject: (id: string, copyId: string) =>
    createTypedAction(ProjectsActionType.DUPLICATED_PROJECT, {
      id,
      copyId,
    }),

  /** `filename: null` to mean failure */
  exportedProject: (id: string, filename: string | null) =>
    createTypedAction(ProjectsActionType.EXPORTED_PROJECT, { id, filename }),
  removedProject: <T>({
    id,
    name,
    restoreAction,
  }: Pick<Project, 'id' | 'name'> & {
    restoreAction: T extends FluxStandardAction<infer U>
      ? FluxStandardAction<U, any>
      : any;
  }) =>
    createTypedAction(ProjectsActionType.REMOVED_PROJECT, {
      id,
      name,
      restoreAction,
    }),
  renamedProject: ({ id, name }: Pick<Project, 'id' | 'name'>) =>
    createTypedAction(ProjectsActionType.RENAMED_PROJECT, {
      id,
      name,
    }),
  restoredProject: ({ id, name }: Pick<Project, 'id' | 'name'>) =>
    createTypedAction(ProjectsActionType.RESTORED_PROJECT, {
      id,
      name,
    }),
  openedMissingProject: ({
    id,
    name,
  }: {
    id: string;
    name: string | undefined;
  }) =>
    createTypedAction(ProjectsActionType.OPENED_MISSING_PROJECT, {
      id,
      name,
    }),
  updatedProjectDiagram: (id: string, diagram: DiagramSnapshot) =>
    createTypedAction(ProjectsActionType.UPDATED_PROJECT_DIAGRAM, {
      id,
      diagram,
    }),
  updatedProjectModelNamespace: (id: string, modelNamespace: string) =>
    createTypedAction(ProjectsActionType.UPDATED_PROJECT_MODEL_NAMESPACE, {
      id,
      modelNamespace,
    }),
  updatedProjectDataNamespace: (id: string, dataNamespace: string) =>
    createTypedAction(ProjectsActionType.UPDATED_PROJECT_DATA_NAMESPACE, {
      id,
      dataNamespace,
    }),

  acceptedProjectDraftDiagram: (id: string, diagram: DiagramSnapshot) =>
    createTypedAction(ProjectsActionType.ACCEPTED_PROJECT_DRAFT_DIAGRAM, {
      id,
      diagram,
    }),
  rejectedProjectDraftDiagram: (id: string, diagram: DiagramSnapshot) =>
    createTypedAction(ProjectsActionType.REJECTED_PROJECT_DRAFT_DIAGRAM, {
      id,
      diagram,
    }),

  addModelsToProjectStarted: ({
    id,
    connectionId,
    databaseId,
    databaseNamespaces,
    modelNamesAndIds,
  }: {
    id: string;
    connectionId: string | null;
    databaseId: string;
    databaseNamespaces: Record<string, string>;
    modelNamesAndIds: Array<Record<'modelId' | 'modelName', string>>;
  }) =>
    createTypedAction(ProjectsActionType.ADD_MODELS_TO_PROJECT_STARTED, {
      id,
      connectionId,
      databaseId,
      databaseNamespaces,
      modelNamesAndIds,
    }),
  addModelsToProjectFinished: (
    id: string,
    modelNamesAndIds: Array<Record<'modelId' | 'modelName', string>>
  ) =>
    createTypedAction(ProjectsActionType.ADD_MODELS_TO_PROJECT_FINISHED, {
      id,
      modelNamesAndIds,
    }),
  addVoiceboxModelToProjectAttempt: (id: string) =>
    createTypedAction(
      ProjectsActionType.ADD_VOICEBOX_MODEL_TO_PROJECT_ATTEMPT,
      { id }
    ),
  addVoiceboxModelToProjectFailure: (id: string) =>
    createTypedAction(
      ProjectsActionType.ADD_VOICEBOX_MODEL_TO_PROJECT_FAILURE,
      { id }
    ),
  addVoiceboxModelToProjectSuccess: (id: string, projectModel: ProjectModel) =>
    createTypedAction(
      ProjectsActionType.ADD_VOICEBOX_MODEL_TO_PROJECT_SUCCESS,
      { id, projectModel }
    ),

  publishProjectStarted: ({
    id,
    databaseId,
    publishedDate,
  }: {
    id: string;
    databaseId: string;
    publishedDate: number;
  }) =>
    createTypedAction(ProjectsActionType.PUBLISH_PROJECT_STARTED, {
      id,
      databaseId,
      publishedDate,
    }),
  publishProjectFinished: ({
    id,
    name,
    databaseId,
    publishedDate,
    intent,
    responses,
    publishHistory,
  }: {
    id: string;
    name: string;
    databaseId: string;
    publishedDate: number;
    intent: Intent;
    responses: PublishProjectResponse[];
    publishHistory: PublishHistory;
  }) =>
    createTypedAction(ProjectsActionType.PUBLISH_PROJECT_FINISHED, {
      id,
      name,
      databaseId,
      publishedDate,
      intent,
      responses,
      publishHistory,
    }),

  uploadedMismatchedHeadersCsv: (filename: string) =>
    createTypedAction(ProjectsActionType.UPLOADED_MISMATCHED_HEADERS_CSV, {
      filename,
    }),
  uploadedPartialCsv: () =>
    createTypedAction(ProjectsActionType.UPLOADED_PARTIAL_CSV),
};

export const projectsStardogRequestDispatchers = {
  requestProjectModelClasses: async ({
    id,
    modelId,
    databaseId,
    namedGraphs,
  }: {
    id: string;
    modelId: string;
    databaseId: string;
    namedGraphs: string[];
  }) =>
    request({
      api: stardogQuery.execute,
      args: [
        databaseId,
        modelClassesQuery,
        undefined,
        { 'default-graph-uri': namedGraphs },
      ],
      attempt: ProjectsActionType.REQUEST_PROJECT_MODEL_CLASSES_ATTEMPT,
      failure: ProjectsActionType.REQUEST_PROJECT_MODEL_CLASSES_FAILURE,
      success: ProjectsActionType.REQUEST_PROJECT_MODEL_CLASSES_SUCCESS,
      action: { id, modelId, databaseId, namedGraphs },
    }),

  requestProjectModelLabelsAndDescriptions: async ({
    id,
    modelId,
    databaseId,
    namedGraphs,
    iris,
  }: {
    id: string;
    modelId: string;
    databaseId: string;
    namedGraphs: string[];
    iris: string[];
  }) =>
    request({
      api: stardogQuery.execute,
      args: [
        databaseId,
        buildLabelsAndDescriptionsQuery(iris),
        MimeType.JSON_LD,
        { 'default-graph-uri': namedGraphs },
      ],
      attempt:
        ProjectsActionType.REQUEST_PROJECT_MODEL_LABELS_AND_DESCRIPTIONS_ATTEMPT,
      failure:
        ProjectsActionType.REQUEST_PROJECT_MODEL_LABELS_AND_DESCRIPTIONS_FAILURE,
      success:
        ProjectsActionType.REQUEST_PROJECT_MODEL_LABELS_AND_DESCRIPTIONS_SUCCESS,
      action: { id, modelId, databaseId, namedGraphs, iris },
    }),
  requestProjectModelProperties: async ({
    id,
    modelId,
    databaseId,
    namedGraphs,
  }: {
    id: string;
    modelId: string;
    databaseId: string;
    namedGraphs: string[];
  }) =>
    request({
      api: stardogQuery.execute,
      args: [
        databaseId,
        modelPropertiesQuery,
        undefined,
        { 'default-graph-uri': namedGraphs },
      ],
      attempt: ProjectsActionType.REQUEST_PROJECT_MODEL_PROPERTIES_ATTEMPT,
      failure: ProjectsActionType.REQUEST_PROJECT_MODEL_PROPERTIES_FAILURE,
      success: ProjectsActionType.REQUEST_PROJECT_MODEL_PROPERTIES_SUCCESS,
      action: { id, modelId, databaseId, namedGraphs },
    }),
  requestProjectModelRules: async ({
    id,
    modelId,
    databaseId,
    namedGraphs,
  }: {
    id: string;
    modelId: string;
    databaseId: string;
    namedGraphs: string[];
  }) =>
    request({
      api: stardogQuery.execute,
      args: [
        databaseId,
        modelRulesQuery,
        undefined,
        { 'default-graph-uri': namedGraphs },
      ],
      attempt: ProjectsActionType.REQUEST_PROJECT_MODEL_RULES_ATTEMPT,
      failure: ProjectsActionType.REQUEST_PROJECT_MODEL_RULES_FAILURE,
      success: ProjectsActionType.REQUEST_PROJECT_MODEL_RULES_SUCCESS,
      action: { id, modelId },
    }),
};

export type ProjectsAction = ActionCreatorAction<
  typeof projectsActionCreators & typeof projectsStardogRequestDispatchers
>;
